import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Entry } from "../entriesAPI";
import {
  postApproveEntries,
  postConfirmEntriesRejected,
} from "../entriesSlice";
import { BottomActionBarContainer } from "../styles";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import { useState } from "react";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import {
  canExpertApproveEntries,
  canExpertEditEntries,
  isExpertAdmin,
} from "../../user/accessRightChecks";
import { isLeaveAttachmentRequiredButMissing } from "../Entry/Shared/functions";
import { FLOATING_CHAT_BUTTON_BOTTOM_RAISED } from "../../messaging/messagingSlice";
import { useFloatingChatButtonBottom } from "../../messaging/useFloatingChatButtonBottom";

type Props = {
  selectedEntries: Entry[];
  setSelectedEntries: (val: Entry[]) => void;
  copyEntry: (val: Entry[] | null) => void;
};

const getFormattedEntries = (
  entries: Entry[],
): { id: string; version: number }[] => {
  const formattedEntries = entries.map(({ id, version }) => ({
    id,
    version,
  }));

  return formattedEntries;
};

const BottomActionBar = ({
  selectedEntries,
  setSelectedEntries,
  copyEntry,
}: Props) => {
  const { t } = useTranslation(["common", "hourEntries"]);
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const canBypassAttachmentCheck = !!user && isExpertAdmin(user);

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    action: string;
  }>({ isOpen: false, action: "" });

  const uniqueEntryProperties = selectedEntries.length
    ? [
        ...new Set(
          selectedEntries.map(({ approvalState, type }) =>
            JSON.stringify({ approvalState, type }),
          ),
        ),
      ].map((selectedEntry) => JSON.parse(selectedEntry))
    : [];

  const barIsVisible =
    selectedEntries.length > 0 && uniqueEntryProperties.length === 1;

  useFloatingChatButtonBottom(
    barIsVisible ? FLOATING_CHAT_BUTTON_BOTTOM_RAISED : null,
  );

  if (!barIsVisible) return null;

  const handleApproveEntries = () => {
    const formattedEntries = getFormattedEntries(selectedEntries);
    dispatch(postApproveEntries(formattedEntries));
    setSelectedEntries([]);
    setConfirmDialog({ isOpen: false, action: "" });
  };

  const handleConfirmEntriesRejected = () => {
    const formattedEntries = getFormattedEntries(selectedEntries);
    dispatch(postConfirmEntriesRejected(formattedEntries));
    setSelectedEntries([]);
    setConfirmDialog({ isOpen: false, action: "" });
  };

  const handleCopyEntry = () => {
    copyEntry(selectedEntries);
  };

  const approvalValue = uniqueEntryProperties[0].approvalState;
  const entryType = uniqueEntryProperties[0].type;
  const areEntriesLong = selectedEntries.every((entry) => !entry.isShift);

  const absenceMissingAttachment =
    !canBypassAttachmentCheck &&
    selectedEntries.some(isLeaveAttachmentRequiredButMissing);

  const showMakeCopy =
    selectedEntries.length === 1 &&
    entryType === "WORK_ENTRY" &&
    areEntriesLong;
  const approved = approvalValue === "Approved" && entryType === "WORK_ENTRY";
  const rejected = approvalValue === "Rejected" && entryType === "WORK_ENTRY";
  const customerRejected =
    approvalValue === "CustomerRejected" && entryType === "WORK_ENTRY";
  const newAbsence = approvalValue === "New" && entryType !== "WORK_ENTRY";
  const approvedAbsence =
    approvalValue === "Approved" && entryType !== "WORK_ENTRY";
  const rejectedAbsence =
    approvalValue === "Rejected" && entryType !== "WORK_ENTRY";
  const isNew = approvalValue === "New" && entryType === "WORK_ENTRY";

  const handleConfirmDialog = (isOpen: boolean, action: string) => {
    setConfirmDialog({ isOpen, action });
  };

  const confirmApprove = confirmDialog.action === "approve";

  return (
    <BottomActionBarContainer>
      <Box>
        {showMakeCopy && (
          <CustomButton
            accessRightChecks={[canExpertEditEntries]}
            sx={{ height: "42px", borderRadius: "20px" }}
            variant="outlined"
            onClick={handleCopyEntry}
          >
            {t("duplicate")}
          </CustomButton>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" gap="1rem">
        {approved && (
          <CustomButton
            accessRightChecks={[canExpertApproveEntries]}
            color="error"
            sx={{ height: "42px", borderRadius: "20px" }}
            variant="outlined"
            onClick={() => handleConfirmDialog(true, "reject")}
          >
            {t("hourEntries:changeToRejected")}
          </CustomButton>
        )}
        {rejected && (
          <>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              color="success"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "approve")}
            >
              {t("hourEntries:changeToApproved")}
            </CustomButton>
          </>
        )}
        {customerRejected && (
          <>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              color="success"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "approve")}
            >
              {t("hourEntries:changeToApproved")}
            </CustomButton>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="contained"
              onClick={() => handleConfirmDialog(true, "reject")}
            >
              {t("hourEntries:confirmRejection")}
            </CustomButton>
          </>
        )}
        {isNew && (
          <>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              color="success"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "approve")}
            >
              {t("common:approve")}
            </CustomButton>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              color="error"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "reject")}
            >
              {t("hourEntries:rejectAndConfirm")}
            </CustomButton>
          </>
        )}
        {newAbsence && (
          <>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              disabled={absenceMissingAttachment}
              color="success"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "approve")}
            >
              {t("common:approve")}
            </CustomButton>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              color="error"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "reject")}
            >
              {t("hourEntries:rejectAndConfirm")}
            </CustomButton>
          </>
        )}
        {approvedAbsence && (
          <CustomButton
            accessRightChecks={[canExpertApproveEntries]}
            color="error"
            sx={{ height: "42px", borderRadius: "20px" }}
            variant="outlined"
            onClick={() => handleConfirmDialog(true, "reject")}
          >
            {t("hourEntries:changeToRejected")}
          </CustomButton>
        )}
        {rejectedAbsence && (
          <>
            <CustomButton
              accessRightChecks={[canExpertApproveEntries]}
              disabled={absenceMissingAttachment}
              color="success"
              sx={{ height: "42px", borderRadius: "20px" }}
              variant="outlined"
              onClick={() => handleConfirmDialog(true, "approve")}
            >
              {t("hourEntries:changeToApproved")}
            </CustomButton>
          </>
        )}
      </Box>
      <ConfirmationDialog
        title={
          confirmApprove
            ? t("hourEntries:entriesApproveConfirmationTitle")
            : t("hourEntries:entriesRejectConfirmationTitle")
        }
        contentText={
          confirmApprove
            ? t("hourEntries:entriesApproveConfirmationContext")
            : t("hourEntries:entriesRejectConfirmationContext")
        }
        confirmText={t("hourEntries:entriesConfirmButton")}
        cancelText={t("hourEntries:entriesCancelButton")}
        isOpen={confirmDialog.isOpen}
        onCancel={() => setConfirmDialog({ isOpen: false, action: "" })}
        onConfirm={() =>
          confirmApprove
            ? handleApproveEntries()
            : handleConfirmEntriesRejected()
        }
      />
    </BottomActionBarContainer>
  );
};

export default BottomActionBar;
